/**
 * Primary left navigation. Renders the {@link NAV_ITEMS} model grouped by
 * section, hiding any entry the live session can't access (RBAC), and marks the
 * active route via TanStack Router's `useRouterState`.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { usePermissions } from "@authe/ui-core/permissions";
import { cn } from "@authe/ui-core";
import { useAskAi } from "@/app/ask-ai-shell";
import { APP_NAME, APP_TAGLINE, NAV_ITEMS, NAV_SECTIONS, ROUTES } from "@/config";

export function Sidebar(): React.ReactElement {
  const { can } = usePermissions();
  const { openAskAi } = useAskAi();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const visible = NAV_ITEMS.filter(
    (item) => !item.resource || can(item.resource, item.action ?? "list"),
  );

  return (
    <aside className="flex h-full w-60 flex-col border-r border-border bg-card">
      <div className="flex items-center gap-2 px-5 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-bold">
          A
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-foreground">{APP_NAME}</div>
          <div className="text-[11px] text-muted-foreground">{APP_TAGLINE}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {NAV_SECTIONS.map((section) => {
          const items = visible.filter((i) => i.section === section);
          if (items.length === 0) return null;
          return (
            <div key={section} className="mb-4">
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section}
              </div>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const active =
                    item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
                  const Icon = item.icon;
                  const className = cn(
                    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  );

                  if (item.to === ROUTES.askAi) {
                    return (
                      <li key={item.to}>
                        <button type="button" className={className} onClick={openAskAi}>
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{item.label}</span>
                        </button>
                      </li>
                    );
                  }

                  return (
                    <li key={item.to}>
                      <Link to={item.to} className={className}>
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
